import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { DateRange } from './screens/detail/periodRange'

// Cross-tab SABER navigation state (v001 §13). The global header pickers were
// removed — each tab owns its own scoping controls — but the *selection* is
// shared so it survives a tab switch and deep-links.
//
// WEB-6567: the URL query string is the source of truth so a formed URL
// deep-links to the same selection and Back/Forward restore it.
// WEB-6581: the selected period (and its Custom range) moved here too, so the
// Dashboard and Shadow Billing tabs share one period — switching tabs keeps it.
// The public API stays a simple useSaberSelection() hook.

// Sentinel for "no specific property selected". Per-property screens treat
// this as "default to the first operating property".
export const ALL_PROPERTIES = 'ALL'

export interface SaberSelection {
  /** Property cuid, or ALL_PROPERTIES when nothing specific is selected. */
  propertyCuid: string
  setPropertyCuid: (cuid: string) => void
  /**
   * Selected period key (a 'q-YYYY-N' quarter, a 'YYYY-MM' month, or 'custom'),
   * or null when unset — screens apply their own default. Shared across the
   * Dashboard and Shadow Billing tabs.
   */
  period: string | null
  /**
   * Set the period key. Pass `seedRange` to write the Custom range in the SAME
   * URL update — react-router's setSearchParams clobbers if called twice in one
   * handler (the second sees a stale prev), so callers must not pair this with a
   * separate setCustomRange in the same tick.
   */
  setPeriod: (key: string, seedRange?: DateRange | null) => void
  /** Custom date range backing period === 'custom', or null when incomplete. */
  customRange: DateRange | null
  setCustomRange: (range: DateRange) => void
}

const SaberContext = createContext<SaberSelection | null>(null)

const pad2 = (n: number): string => String(n).padStart(2, '0')

/** First of the current month as a YYYY-MM-DD string. */
export const currentMonthStart = (): string => {
  const now = new Date()
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-01`
}

/** The previous calendar month as a 'YYYY-MM' string. */
export const previousMonthYm = (): string => {
  const now = new Date()
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return `${prev.getFullYear()}-${pad2(prev.getMonth() + 1)}`
}

/** First of the previous calendar month as a YYYY-MM-DD string. */
export const previousMonthStart = (): string => `${previousMonthYm()}-01`

// URL query-param keys backing the cross-tab selection.
const PROPERTY_PARAM = 'property'
const PERIOD_PARAM = 'period'
const FROM_PARAM = 'from'
const TO_PARAM = 'to'

export const SaberProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const propertyCuid = searchParams.get(PROPERTY_PARAM) ?? ALL_PROPERTIES
  const period = searchParams.get(PERIOD_PARAM)
  const fromParam = searchParams.get(FROM_PARAM)
  const toParam = searchParams.get(TO_PARAM)
  // Memoized so the context value below only changes when the params do —
  // an inline conditional would mint a new object every render.
  const customRange = useMemo<DateRange | null>(
    () =>
      fromParam != null && fromParam !== '' && toParam != null && toParam !== ''
        ? { from: fromParam, to: toParam }
        : null,
    [fromParam, toParam]
  )

  // Changing the property is a selection → push a history entry (Back restores
  // the previous one). ALL_PROPERTIES / '' clears the param to keep URLs clean.
  const setPropertyCuid = useCallback(
    (cuid: string): void => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (cuid === ALL_PROPERTIES || cuid === '') next.delete(PROPERTY_PARAM)
        else next.set(PROPERTY_PARAM, cuid)
        return next
      })
    },
    [setSearchParams]
  )

  // Period / Custom-range tweaks replace the history entry (they read like the
  // old local-state pickers — a chip click should not pile up Back stops) while
  // still persisting in the URL so the selection survives a tab switch.
  const setPeriod = useCallback(
    (key: string, seedRange?: DateRange | null): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set(PERIOD_PARAM, key)
          if (seedRange != null && seedRange.from !== '' && seedRange.to !== '') {
            next.set(FROM_PARAM, seedRange.from)
            next.set(TO_PARAM, seedRange.to)
          } else if (key !== 'custom') {
            next.delete(FROM_PARAM)
            next.delete(TO_PARAM)
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setCustomRange = useCallback(
    (range: DateRange): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set(FROM_PARAM, range.from)
          next.set(TO_PARAM, range.to)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const value = useMemo<SaberSelection>(
    () => ({ propertyCuid, setPropertyCuid, period, setPeriod, customRange, setCustomRange }),
    [propertyCuid, period, customRange, setPropertyCuid, setPeriod, setCustomRange]
  )

  return <SaberContext.Provider value={value}>{children}</SaberContext.Provider>
}

/** Read the shared SABER selection. Must be used under a SaberProvider. */
export const useSaberSelection = (): SaberSelection => {
  const ctx = useContext(SaberContext)
  if (ctx == null) {
    throw new Error('useSaberSelection must be used within a SaberProvider')
  }
  return ctx
}
